import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FaHeart } from "react-icons/fa";
import { useCart } from "../context/CartContext";
import api from "../api/api";
import { getImageUrl } from "../utils/imageUrl";
import { ENABLE_CART_UI } from "../config/featureFlags";

const ProductCard = ({ product, currentLanguage, user, wishlist }) => {
  const { t } = useTranslation();
  const { cartItems, addItemToCart, toggleSidebar } = useCart();
  const [isFavorited, setIsFavorited] = useState(false);

  const itemInCart = cartItems.find(
    (item) => item.product_id === product.product_id,
  );
  const cartQuantity = itemInCart ? itemInCart.quantity : 0;
  const isMaxStock = cartQuantity >= product.stock_quantity;
  const isOutOfStock = product.stock_quantity === 0;

  useEffect(() => {
    if (!wishlist || !product) return;

    const isProductInWishlist = wishlist.some(
      (item) => item.product_id === product.product_id,
    );
    setIsFavorited(isProductInWishlist);
  }, [wishlist, product]);

  const handleFavoriteClick = async (event) => {
    event.stopPropagation();

    if (!user) {
      console.log("Please log in to add products to your wishlist.");
      return;
    }

    try {
      const response = await api.post("/user/wishlist/toggle", {
        productId: product.product_id,
      });

      setIsFavorited(response.data.action === "added");
    } catch (error) {
      console.error("Failed to toggle wishlist:", error);
    }
  };

  const handleAddToCartClick = async (event) => {
    event.stopPropagation();

    if (!user) {
      console.log("Please log in to add products to your cart.");
      return;
    }

    if (isOutOfStock) {
      alert(t("outOfStock", "This product is out of stock"));
      return;
    }

    if (isMaxStock) {
      alert(
        t(
          "maxStockInCart",
          `You already have the maximum available stock (${product.stock_quantity}) in your cart`,
        ),
      );
      return;
    }

    await addItemToCart(product.product_id, 1);
    toggleSidebar(true);
  };

  const productName = product[`name_${currentLanguage}`] || product.name_en;
  const stockQuantity = product.stock_quantity;
  const productTags = Array.isArray(product.tags)
    ? product.tags.slice(0, 1)
    : [];

  const imageUrl =
    product.images && product.images[0]
      ? getImageUrl(product.images[0].image_url)
      : "https://placehold.co/400x300/E0BBE4/FFFFFF?text=No+Image";

  const renderCartButton = () => {
    if (isOutOfStock) {
      return (
        <button
          disabled
          className="flex-grow cursor-not-allowed rounded-xl bg-gray-400 px-3 py-2.5 text-sm font-bold text-white opacity-60"
        >
          {t("outOfStock", "Out of Stock")}
        </button>
      );
    }

    if (isMaxStock) {
      return (
        <button
          disabled
          className="flex-grow cursor-not-allowed rounded-xl bg-orange-400 px-3 py-2.5 text-sm font-bold text-white opacity-75"
        >
          {t("maxInCart", "Max in Cart")}
        </button>
      );
    }

    return (
      <button
        onClick={handleAddToCartClick}
        className="flex-grow rounded-xl bg-[#AA7F96] px-3 py-2.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#916A80] focus:outline-none focus:ring-2 focus:ring-[#AA7F96] focus:ring-opacity-50"
      >
        {t("product.addToCart", "Add to Cart")}
      </button>
    );
  };

  return (
    <Link
      to={`/product/${product.product_slug || product.product_id}`}
      state={{ product }}
      className="bg-[white] group relative flex cursor-pointer flex-col overflow-hidden rounded-[1.35rem] border border-white/80 bg-white/88 shadow-[0_16px_34px_-24px_rgba(131,55,91,0.45)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_44px_-28px_rgba(131,55,91,0.55)]"
    >
      <div className="absolute inset-x-5 top-0 h-16 rounded-b-[1.5rem] bg-gradient-to-b opacity-70 pointer-events-none" />

      <div className="relative h-[14rem] w-full overflow-hidden">
        <img
          src={imageUrl}
          alt={productName}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06] "
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2c1d24]/18 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

        {productTags.length > 0 && (
          <div className="absolute left-3 top-3 flex max-w-[72%] flex-wrap gap-1.5 hidden lg:flex">
            {productTags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-white/70 bg-white/90 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#83375B] shadow-sm backdrop-blur"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <button
          onClick={handleFavoriteClick}
          className={`absolute right-3 top-3 rounded-full p-2.5 shadow-sm backdrop-blur transition-colors duration-200 ${
            isFavorited
              ? "bg-white text-[rgb(153_46_26)] hover:bg-[#f4e4e4]"
              : "bg-white text-gray-600 hover:bg-[#f1e7eb]"
          }`}
          title={t("product.addToFavorites")}
        >
          <FaHeart className="h-4 w-4" />
        </button>
      </div>

      <div className="flex flex-grow flex-col p-4">
        <h3
          className="mb-2 h-[42px] overflow-hidden text-lg font-normal leading-[21px] text-[#2f2328] line-clamp-2"
          title={productName}
        >
          {productName}
        </h3>

        {/* {productTags.length > 0 && (
          <div className="mb-2.5 flex flex-wrap gap-1.5">
            {productTags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-[#f7eaf0] px-2 py-0.5 text-[10px] font-medium text-[#83375B]"
              >
                {tag}
              </span>
            ))}
          </div>
        )} */}

        <div className="mb-2.5 flex items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            {product.sale_price ? (
              <>
                <p className="text-xs line-through text-gray-500">
                  {product.price} TL
                </p>
                <p className="text-lg font-bold text-red-600">
                  {product.sale_price} TL
                </p>
              </>
            ) : (
              <p className="text-lg font-bold text-[#83375B]">
                {product.price} TL
              </p>
            )}
          </div>
        </div>

        <p className="mb-3 flex-grow text-xs leading-5 text-[#76656d]">
          {isOutOfStock
            ? t("outOfStock", "Out of Stock")
            : `${t("inStock", "In Stock")}: ${stockQuantity}`}
        </p>

        {isMaxStock && !isOutOfStock && (
          <p className="mb-2 text-[11px] font-semibold text-orange-500">
            {t("maxStockInCart", "Max stock in cart")}
          </p>
        )}

        <div className="flex h-[40px] items-center space-x-2">
          {ENABLE_CART_UI && renderCartButton()}
          <Link
            to={`/product/${product.product_slug || product.product_id}`}
            state={{ product }}
            className={`rounded-xl bg-[#f3eef1] px-3 py-2.5 text-sm text-gray-700 transition-colors hover:bg-[#e8dde3] ${
              ENABLE_CART_UI ? "" : "w-full"
            }`}
          >
            {t("product.view", "View this Product")}
          </Link>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
